/**
 * BIM-style Material System — document data model.
 *
 * A material here is NOT just a visual skin: like in BIM, it is a BIM asset
 * carrying five sections of information:
 *   Identity   → schedule / tag / specification text data (class, manufacturer, cost...)
 *   Graphics   → 2D representation (shading color, surface & cut fill patterns)
 *   Appearance → 3D render asset (shared BY REFERENCE — see appearanceAssetId)
 *   Physical   → structural/engineering values (density, modulus, yield strength...)
 *   Thermal    → energy analysis values (conductivity, heat capacity, U/R helpers)
 *
 * The reference doc for this design lives in docs/architecture/bim-material-system.md.
 */

// ==================== Fill Patterns (Graphics 2D) ====================

export type FillPatternStyle =
    | 'Solid'        // fully filled background
    | 'Parallel'     // one-direction hatch lines
    | 'Crosshatch'   // two perpendicular hatch sets
    | 'Brick'        // running-bond brick courses (horizontal lines + staggered ticks)
    | 'Blocks'       // square block grid (concrete block masonry)
    | 'ANSI31'       // diagonal hatch alias kept for pattern-name familiarity
    | 'Dots'         // stipple dots
    | 'None';        // no pattern (background only)

export interface FillPattern {
    id: string;
    name: string;
    style: FillPatternStyle;
    /** Hatch line angle in degrees (0 = horizontal). */
    angle: number;
    /** Pattern repeat spacing in meters (model space). */
    spacing: number;
    /** Line width multiplier relative to spacing. */
    thickness: number;
    /** Built-in patterns cannot be deleted from the browser. */
    builtIn: boolean;
}

/** Static catalog of model fill patterns (BIM built-ins analog). */
export const FILL_PATTERNS: FillPattern[] = [
    { id: 'fp-solid', name: 'Solid Fill', style: 'Solid', angle: 0, spacing: 0.01, thickness: 1, builtIn: true },
    { id: 'fp-none', name: 'No Background', style: 'None', angle: 0, spacing: 0.01, thickness: 0, builtIn: true },
    { id: 'fp-parallel-45', name: 'Parallel 45°', style: 'Parallel', angle: 45, spacing: 0.01, thickness: 0.08, builtIn: true },
    { id: 'fp-parallel-0', name: 'Parallel 0°', style: 'Parallel', angle: 0, spacing: 0.01, thickness: 0.08, builtIn: true },
    { id: 'fp-parallel-90', name: 'Parallel 90°', style: 'Parallel', angle: 90, spacing: 0.01, thickness: 0.08, builtIn: true },
    { id: 'fp-ansi31', name: 'ANSI31', style: 'Parallel', angle: 45, spacing: 0.008, thickness: 0.08, builtIn: true },
    { id: 'fp-crosshatch', name: 'Crosshatch', style: 'Crosshatch', angle: 45, spacing: 0.012, thickness: 0.08, builtIn: true },
    { id: 'fp-earth', name: 'Earth', style: 'Crosshatch', angle: 30, spacing: 0.015, thickness: 0.06, builtIn: true },
    { id: 'fp-brick', name: 'Brick Stack Bond', style: 'Brick', angle: 0, spacing: 0.02, thickness: 0.1, builtIn: true },
    { id: 'fp-block', name: 'Concrete Block Stack', style: 'Blocks', angle: 0, spacing: 0.04, thickness: 0.06, builtIn: true },
    { id: 'fp-concrete', name: 'Concrete (Sand)', style: 'Dots', angle: 0, spacing: 0.008, thickness: 0.25, builtIn: true },
    { id: 'fp-gypsum', name: 'Gypsum', style: 'Parallel', angle: 0, spacing: 0.03, thickness: 0.05, builtIn: true },
    { id: 'fp-insulation', name: 'Thermal Insulation', style: 'Crosshatch', angle: 60, spacing: 0.02, thickness: 0.05, builtIn: true },
];

export const getFillPattern = (patternId: string): FillPattern | undefined =>
    FILL_PATTERNS.find((p) => p.id === patternId || p.name === patternId);

// ==================== Identity ====================

/** BIM material classes used to group materials in the browser tree. */
export const MATERIAL_CLASSES = [
    'Concrete',
    'Masonry',
    'Metal',
    'Wood',
    'Plastics',
    'Finishes',
    'Glass',
    'Membrane',
    'Miscellaneous',
] as const;

export type MaterialClass = (typeof MATERIAL_CLASSES)[number];

export interface MaterialIdentity {
    /** Schedule / specification description text. */
    description: string;
    class: MaterialClass;
    manufacturer: string;
    /** Product code / catalog number. */
    typeMark: string;
    model: string;
    /** Cost per unit as text so any currency/format can be typed (BIM parity). */
    cost: string;
    costBasis: string; // e.g. 'm', 'm²', 'm³', 'each'
    /** Takeoff quantity unit for volume/area/weight extraction. */
    takeoffUnit: 'each' | 'm' | 'm²' | 'm³' | 'kg' | 'ton';
    /** Takeoff waste multiplier (1.05 = 5% waste). */
    wasteFactor: number;
    /** Keynote code used by keynoting / tagging systems. */
    keynote: string;
    /** Optional page reference to specification document. */
    pageNumber: string;
}

// ==================== Graphics (2D) ====================

export interface MaterialGraphics {
    /** Push the render-appearance average color into the 2D shading. */
    useRenderAppearance: boolean;
    /** Explicit 2D shading color (hex) used when useRenderAppearance is false. */
    shadingColor: string;
    /** Foreground hatch color (hex). */
    foregroundColor: string;
    /** Background / paper color behind the hatch (hex). */
    backgroundColor: string;
    /** Fill pattern id or name shown on cut surfaces (plan cut). */
    cutPatternId: string;
    /** Fill pattern id or name projected onto visible surfaces. */
    surfacePatternId: string;
}

// ==================== Appearance (3D render asset) ====================

export interface AppearanceAsset {
    id: string;
    name: string;
    /** Diffuse/base color (hex). */
    color: string;
    /** 0..1 — higher is rougher (PBR roughness). */
    roughness: number;
    /** 0..1 — metalness for PBR shading. */
    metalness: number;
    /** 0..1 surface transparency. */
    transparency: number;
    /** Image-library entry id used as render texture ('' = flat color). */
    imageId: string;
    /** Texture tiling size in meters — repeat of one image tile on a surface. */
    tileSize: number;
    /** Procedural bump relief strength 0..1 (joint/relief feel without textures). */
    bumpStrength: number;
    /** Self-illumination color multiplier (hex) — 'none' when ''. */
    emissionColor: string;
    /** Emission intensity multiplier. */
    emissionStrength: number;
}

// ==================== Physical (statics & analysis) ====================

export interface MaterialPhysical {
    /** kg/m³. */
    density: number;
    /** MPa. */
    youngsModulus: number;
    /** MPa. */
    shearModulus: number;
    poissonRatio: number;
    /** MPa — structural steel & co. */
    yieldStrength: number;
    /** MPa — concrete compression. */
    compressionStrength: number;
    /** 1/K thermal expansion coefficient. */
    thermalExpansion: number;
    /** Acoustic absorption coefficient 0..1. */
    acousticAbsorption: number;
    /** Fire reaction Euroclass letter (A1, A2, B, C, D, E, F). */
    fireClass: string;
    /** Electromagnetic/thermal reflectivity 0..1. */
    reflectivity: number;
}

// ==================== Thermal (energy analysis) ====================

export interface MaterialThermal {
    /** Thermal conductivity W/(m·K). */
    conductivity: number;
    /** Specific heat capacity J/(kg·K). */
    specificHeat: number;
    /** Emissivity 0..1. */
    emissivity: number;
    /** Permeability (ng/(Pa·s·m)) for vapor analysis. */
    permeability: number;
    /** Thermal absorptivity 0..1 (surface). */
    thermalAbsorptance: number;
}

/** U-value helper: U = k / d  [W/(m²·K)] */
export const computeUValue = (conductivity: number, thicknessM: number): number =>
    thicknessM > 0 ? conductivity / thicknessM : 0;

/** R-value helper: R = d / k  [(m²·K)/W] */
export const computeRValue = (conductivity: number, thicknessM: number): number =>
    conductivity > 0 ? thicknessM / conductivity : 0;

// ==================== The Material record ====================

export interface BIMMaterial {
    id: string;
    /** Shown in browser tree, schedules and tags. Unique-ish per project. */
    name: string;
    /**
     * Appearance asset reference (BY REFERENCE, like a BIM Asset).
     * Several materials may share one asset; editing the asset affects all.
     * 'Duplicate this asset' in the UI mints an independent copy.
     */
    appearanceAssetId: string;
    identity: MaterialIdentity;
    graphics: MaterialGraphics;
    physical: MaterialPhysical;
    thermal: MaterialThermal;
}

/** Shallow patch helper types for kernel update actions. */
export type MaterialSectionPatch<S> = Partial<S>;

/**
 * Default Object Styles material per category: stores a material NAME for
 * persistence compatibility with legacy type-parameter strings.
 */
export interface CategoryMaterialStyle {
    materialName: string;
}

// ==================== Factory helpers ====================

export const defaultIdentity = (cls: MaterialClass = 'Miscellaneous'): MaterialIdentity => ({
    description: '',
    class: cls,
    manufacturer: '',
    typeMark: '',
    model: '',
    cost: '',
    costBasis: 'm²',
    takeoffUnit: 'm²',
    wasteFactor: 1.05,
    keynote: '',
    pageNumber: '',
});

export const defaultGraphics = (overrides: Partial<MaterialGraphics> = {}): MaterialGraphics => ({
    useRenderAppearance: true,
    shadingColor: '#B0B0B0',
    foregroundColor: '#303030',
    backgroundColor: '#F0F0F0',
    cutPatternId: 'fp-none',
    surfacePatternId: 'fp-none',
    ...overrides,
});

export const defaultPhysical = (overrides: Partial<MaterialPhysical> = {}): MaterialPhysical => ({
    density: 1000,
    youngsModulus: 0,
    shearModulus: 0,
    poissonRatio: 0.25,
    yieldStrength: 0,
    compressionStrength: 0,
    thermalExpansion: 0.000012,
    acousticAbsorption: 0,
    fireClass: 'A1',
    reflectivity: 0.2,
    ...overrides,
});

export const defaultThermal = (overrides: Partial<MaterialThermal> = {}): MaterialThermal => ({
    conductivity: 0.5,
    specificHeat: 1000,
    emissivity: 0.9,
    permeability: 0,
    thermalAbsorptance: 0.9,
    ...overrides,
});
